'use client';
import { useState, useRef, useEffect } from 'react';
import { authClient } from '@bip/api-client';
import Swal from 'sweetalert2';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onReopen: () => void;
}

type Step = 'email' | 'login' | 'register';

export function LoginModal({ isOpen, onClose, onReopen }: Props) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const [validated, setValidated] = useState(false);

  const handleClose = () => {
    setStep('email');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setInfo('');
    setPasswordError('');
    setLoading(false);
    setValidated(false);
    setShowPwd(false);
    setShowConfirmPwd(false);
    onClose();
  };

  const reopenAtEmail = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setInfo('');
    setValidated(false);
    setShowPwd(false);
    setShowConfirmPwd(false);
    onReopen();
  };

  const showVerificationSwal = (emailAddr: string) => {
    Swal.fire({
      icon: 'success',
      title: 'Verification Link Sent',
      html: `We have emailed a verification link to <b>${emailAddr}</b>. The link will be valid for 24 hours.<br><br>No email received? Please check your junk or spam folders, or click "<b>Resend</b>" to receive another activation email.<br><br>For further support, please contact <b>reservations@bookitplease.com</b>.`,
      confirmButtonText: 'Resend',
      confirmButtonColor: '#3c0a52',
      cancelButtonText: 'Return to Login',
      showCancelButton: true,
      allowOutsideClick: false,
      customClass: {
        confirmButton: 'swal-btn-rounded',
        cancelButton: 'swal-btn-rounded swal-btn-black',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        authClient.checkEmailVerification(emailAddr).then(() => {
          Swal.fire({
            icon: 'success',
            title: 'A new verification email has been sent!',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
        });
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        reopenAtEmail();
      }
    });
  };

  const handleContinue = async () => {
    setValidated(true);
    const normalizedEmail = email.trim().toLowerCase();
    setEmail(normalizedEmail);
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) return;
    setLoading(true);
    setError('');
    try {
      const res = await authClient.checkMemberExistence(normalizedEmail);
      setValidated(false);
      if (res?.message === 'success') setStep('login');
      else setStep('register');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setValidated(true);
    setPasswordError('');
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      const vRes = await authClient.checkEmailVerification(email);

      if (vRes?.message === 'Email verification pending') {
        setLoading(false);
        handleClose();
        Swal.fire({
          title: 'Check your inbox',
          html: `We have emailed a verification link to <b>${email}</b>. The link will be valid for 24 hours.<br><br>No email received? Please check your junk or spam folders, or click "<b>Resend</b>" to receive another activation email.<br><br>For further support, please contact <b>reservations@bookitplease.com</b>.`,
          confirmButtonText: 'Resend',
          confirmButtonColor: '#3c0a52',
          cancelButtonText: 'Return to Login',
          showCancelButton: true,
          allowOutsideClick: false,
          customClass: {
            confirmButton: 'swal-btn-rounded',
            cancelButton: 'swal-btn-rounded swal-btn-black',
          },
        }).then((result) => {
          if (result.isConfirmed) {
            authClient.checkEmailVerification(email).then(() => {
              Swal.fire({
                icon: 'success',
                title: 'A new verification email has been sent!',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
              });
            });
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            reopenAtEmail();
          }
        });
        return;
      }

      const lRes = await authClient.login(email, password);

      if (lRes?.message === 'success') {
        if (window.location.pathname === '/verifyemail') {
          window.location.href = '/';
        } else {
          window.location.reload();
        }
      } else {
        setPasswordError(lRes?.displayMessage || 'Incorrect password.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setValidated(true);
    if (!password.trim() || !confirmPassword.trim()) return;
    const pwdRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+\-[\]()€£]).{8,}$/;
    if (!pwdRegex.test(password)) return;
    if (password !== confirmPassword) {
      setError('Password does not match.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authClient.signup(email, password);
      if (res?.message === 'success') {
        handleClose();
        showVerificationSwal(email);
      } else {
        setError(res?.displayMessage || 'Signup failed.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setLoading(true);
    try {
      await authClient.forgotPassword(email);
      setLoading(false);
      handleClose();
      Swal.fire({
        title: 'Check your inbox',
        html: `We have emailed a password reset link to <b>${email}</b>. The link will be valid for 10 minutes.<br><br>No email received? Check your junk/spam folder. If you still need help, contact <b>reservations@bookitplease.com</b>.`,
        confirmButtonText: 'Back to sign-in',
        confirmButtonColor: '#3c0a52',
        allowOutsideClick: false,
        customClass: {
          confirmButton: 'swal-btn-rounded',
        },
      }).then((result) => {
        if (result.isConfirmed) {
          reopenAtEmail();
        }
      });
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // Hide body scrollbar when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop fade show" onClick={handleClose} />

      <div
        id="register"
        role="dialog"
        aria-labelledby="exampleModalLabel"
        className="modal fade show d-block"
        tabIndex={-1}
      >
        <div role="document" className="modal-dialog">
          <div className="modal-content border-0">
            <div className="modal-header">
              {step === 'email' && (
                <h5 id="signHead" className="modal-title">
                  Sign in or create an account
                </h5>
              )}

              {step === 'register' && (
                <h5 id="passwordHead" className="modal-title">
                  Create password
                </h5>
              )}

              {step === 'login' && (
                <h5 id="enterPassword" className="modal-title">
                  <span className="grey-color pr-2">
                    <a
                      href="#"
                      className="grey-color"
                      onClick={(e) => {
                        e.preventDefault();
                        setStep('email');
                        setPassword('');
                        setPasswordError('');
                        setError('');
                        setInfo('');
                        setValidated(false);
                        setShowPwd(false);
                        setShowConfirmPwd(false);
                      }}
                    >
                      <i className="fa-solid fa-arrow-left-long"></i>
                    </a>
                  </span>
                  Enter your password
                </h5>
              )}

              <button
                type="button"
                aria-label="Close"
                className="close formCloseModalButton"
                onClick={handleClose}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              {info && <div className="alert alert-info py-2">{info}</div>}

              {step === 'login' && (
                <p className="mb-3">
                  This account is already linked to an existing account. Please enter your password
                  to sign in.
                </p>
              )}

              <form
                ref={formRef}
                id="registrationForm"
                noValidate
                className=""
                onSubmit={(e) => e.preventDefault()}
              >
                {/* Email — shown on email step only */}
                {step === 'email' && (
                  <div className="form-group pt-1">
                    <label htmlFor="Emailaddress" className="fontweight500">
                      Email Address
                    </label>
                    <div className="position-relative">
                      <input
                        id="Emailaddress"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="Enter your email address"
                        required
                        className={`form-control padlefft${validated && !email.trim() ? ' is-invalid' : validated && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? ' is-invalid' : validated && email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? ' is-valid' : ''}`}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => setValidated(true)}
                      />
                      <div className="loginicon">
                        <i className="fa-light fa-envelope grey-color"></i>
                      </div>
                      {validated && !email.trim() && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                          Please fill out this field.
                        </div>
                      )}
                      {validated && email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                          Please enter valid email address.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Login Password */}
                {step === 'login' && (
                  <div className="form-group pt-1">
                    <label htmlFor="loginPassword" className="fontweight500">
                      Password
                    </label>
                    <div className="input-group mb-1" style={{ flexWrap: 'wrap' }}>
                      <input
                        aria-label="Enter a password"
                        id="loginPassword"
                        name="password"
                        autoComplete="current-password"
                        type={showPwd ? 'text' : 'password'}
                        placeholder="Type your password"
                        className={`form-control${validated && !password.trim() ? ' is-invalid' : passwordError ? ' is-invalid' : ''}`}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setPasswordError('');
                        }}
                        onBlur={() => setValidated(true)}
                      />
                      <div className="input-group-append">
                        <span
                          className="input-group-text"
                          onClick={() => setShowPwd((v) => !v)}
                          style={{ cursor: 'pointer' }}
                        >
                          <i
                            className={`fa-light eye-pos ${showPwd ? 'fa-eye' : 'fa-eye-slash'} grey-color`}
                          />
                        </span>
                      </div>
                      {validated && !password.trim() && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                          Please fill out this field.
                        </div>
                      )}
                      {passwordError && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                          {passwordError}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Create Password */}
                {step === 'register' && (
                  <>
                    <div className="form-group pt-1">
                      <label htmlFor="passwordPrimary" className="fontweight500">
                        Password{' '}
                        <span className="info-tooltip-wrapper">
                          <i className="fa fa-info-circle"></i>
                          <span className="info-tooltip-text">
                            Password must have: 8 characters minimum, 1 upper case, 1 lower case, 1
                            special character, 1 number. Special characters include: ! @ # $ £ % ^
                            &amp; * ( ) [ ].
                          </span>
                        </span>
                      </label>
                      <div className="input-group" style={{ flexWrap: 'wrap' }}>
                        <input
                          aria-label="Enter a password"
                          id="passwordPrimary"
                          name="new-password"
                          autoComplete="new-password"
                          type={showPwd ? 'text' : 'password'}
                          placeholder="Type your password"
                          className={`form-control${validated && !password.trim() ? ' is-invalid' : validated && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+\-[\]()€£]).{8,}$/.test(password) ? ' is-invalid' : validated && /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+\-[\]()€£]).{8,}$/.test(password) ? ' is-valid' : ''}`}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onBlur={() => setValidated(true)}
                        />
                        <div className="input-group-append">
                          <span
                            className="input-group-text"
                            onClick={() => setShowPwd((v) => !v)}
                            style={{ cursor: 'pointer' }}
                          >
                            <i
                              className={`fa-light eye-pos ${showPwd ? 'fa-eye' : 'fa-eye-slash'} grey-color`}
                            />
                          </span>
                        </div>
                        {validated && !password.trim() && (
                          <div className="invalid-feedback" style={{ display: 'block' }}>
                            Please fill out this field.
                          </div>
                        )}
                        {validated &&
                          password.trim() &&
                          !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+\-[\]()€£]).{8,}$/.test(
                            password,
                          ) && (
                            <div className="invalid-feedback" style={{ display: 'block' }}>
                              Does not match password criteria
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="form-group pt-1">
                      <label htmlFor="confirmPassword" className="fontweight500">
                        Confirm Password
                      </label>
                      <div className="input-group" style={{ flexWrap: 'wrap' }}>
                        <input
                          id="confirmPassword"
                          name="confirm-password"
                          autoComplete="new-password"
                          type={showConfirmPwd ? 'text' : 'password'}
                          placeholder="Type your password"
                          className={`form-control${validated && !confirmPassword.trim() ? ' is-invalid' : validated && confirmPassword !== password ? ' is-invalid' : validated && confirmPassword === password && confirmPassword.trim() && /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+\-[\]()€£]).{8,}$/.test(password) ? ' is-valid' : ''}`}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          onBlur={() => setValidated(true)}
                        />
                        <div className="input-group-append">
                          <span
                            className="input-group-text"
                            onClick={() => setShowConfirmPwd((v) => !v)}
                            style={{ cursor: 'pointer' }}
                          >
                            <i
                              className={`fa-light eye-pos ${showConfirmPwd ? 'fa-eye' : 'fa-eye-slash'} grey-color`}
                            />
                          </span>
                        </div>
                        {validated && !confirmPassword.trim() && (
                          <div className="invalid-feedback" style={{ display: 'block' }}>
                            Please fill out this field.
                          </div>
                        )}
                        {validated && confirmPassword.trim() && confirmPassword !== password && (
                          <div className="invalid-feedback" style={{ display: 'block' }}>
                            Password does not match.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Buttons */}
                {step === 'email' && (
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      disabled={loading}
                      onClick={handleContinue}
                    >
                      {loading ? 'Please wait…' : 'Continue with email'}
                    </button>
                  </div>
                )}

                {step === 'register' && (
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      disabled={loading}
                      onClick={handleSignup}
                    >
                      {loading ? 'Please wait…' : 'Create account'}
                    </button>
                  </div>
                )}

                {step === 'login' && (
                  <>
                    <div className="text-center">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        disabled={loading}
                        onClick={handleLogin}
                      >
                        {loading ? 'Please wait…' : 'Sign In'}
                      </button>
                    </div>
                    <p className="pt-2 fontsize12 text-center">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleForgot();
                        }}
                      >
                        Forgotten your password?
                      </a>
                    </p>
                  </>
                )}

                <hr />

                <p className="text-center fontsize14">
                  By signing in or creating account, you agree with our{' '}
                  <a href="/terms-and-conditions">Terms &amp; Conditions</a> and{' '}
                  <a href="/privacy-policy">Privacy Policy</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
